import { BehaviorSubject } from 'rxjs';
import { t } from '../../../utils/helpers';
import Color from './color';
import { ITable, BaseGameState } from './interfaces';
import { Message } from '../../chat/message';
import { TableSides } from './types';
import { IPlayer } from '@base/features/player/model/interfaces';
import Codes from '@base/features/socket/codes';

export const MAX_PLAYERS = 12;

export default abstract class BaseTable implements ITable {
  canBeWatched() {
    return (
      !this.noWatchers && // Разрешены наблюдатели
      this.getCost() <= 0 && // Не платный стол
      this.isAllPlaying()
    ); // Все игроки присутствуют
  }

  canBePlayed() {
    return Boolean(!this.isAllPlaying() && !this.inviteOnly);
  }

  getSign(sign: number) {
    return Boolean(sign & this.signs);
  }

  onOptionsChanged? = 'onOptionsChanged';

  // Первичные параметры, получаемые от сервера
  tid = -1;
  inviteOnly = false;
  timeGame = 0;
  timeGameAdd = 0;
  ratingGame = false;
  cost = 0;
  reserved = 0;
  gameColor = 0;
  withTime = false;
  timePerMove = 0;
  withTimePerMove = false;
  maxPoints = 0;
  minPoints = 0;
  ladder = false;
  noWatchers = false;
  signs = 0;

  // Дополнительные параметры стола
  seats: IPlayer[] = [];
  //seats?: any;
  owner?: IPlayer;
  id?: number;
  gameState?: BaseGameState;
  chatMessages: Message[] = [];
  onChatMessages = new BehaviorSubject<Message[]>([]);

  addChatMessage(msg: Message): void {
    this.chatMessages.push(msg);
    this.onChatMessages.next([...this.chatMessages]);
  }

  clearChatMessages(): void {
    this.chatMessages.length = 0;
  }

  getMaxPlayersCount(): number {
    return 2;
  }

  getPlayerBySeat(seat: number): IPlayer | undefined {
    if (this.seats) return this.seats[seat];
  }

  getPlayerByTableSide(side: TableSides): IPlayer | undefined {
    const color = this.gameState?.getColorForSide(side);
    return color ? this.getPlayerByColor(color) : undefined;
  }

  getCurrentTurnPlayer(): IPlayer | undefined {
    const currentTurnSide = this.gameState?.currentTurn;
    return currentTurnSide ? this.getPlayerByTableSide(currentTurnSide) : undefined;
  }

  getPlayerByColor(color: Color): IPlayer | undefined {
    return color === Color.Black ? this.gameState?.getBlackPlayer() : this.gameState?.getWhitePlayer();
    // return this.seats[color === Color.Black ? 1 : 0];
  }

  setPlayer(player: IPlayer, seat: number): void {
    const previousValue = this.seats[seat];
    this.seats[seat] = player;

    if (this.gameState && seat < this.getMaxPlayersCount()) {
      if (!player) {
        this.gameState.onPlayerLeft(previousValue);
      } else {
        this.gameState.onPlayerJoined(player);
      }
    }
  }

  swapPlayers(p1: IPlayer, p2: IPlayer) {
    this.seats[0] = p1;
    this.seats[1] = p2;
  }

  setOwner(player: IPlayer): void {
    this.owner = player;
  }

  getOwner() {
    return this.owner;
  }

  getWhitePlayer(): IPlayer {
    return this.seats[0];
  }

  getBlackPlayer(): IPlayer {
    return this.seats[1];
  }

  getAnyPlayer(): IPlayer {
    return this.getWhitePlayer() || this.getBlackPlayer();
  }

  getPlayers() {
    return this.seats;
  }

  isAllPlaying(): boolean {
    for (let i = 0; i < this.getMaxPlayersCount(); i++) {
      if (this.seats && !this.seats[i]) return false;
    }
    return true;
  }

  getCountOfPlayers(excludeBots?: boolean): number {
    let count = 0;
    for (let i = 0; i < this.getMaxPlayersCount(); i++) {
      if (this.seats[i]) {
        if (!excludeBots || !this.seats[i].isBot()) {
          count++;
        }
      }
    }
    return count;
  }

  hasPlayers() {
    for (let i = 0; i < this.getMaxPlayersCount(); i++) if (this.seats && this.seats[i]) return true;
    return false;
  }

  hasPeople(): boolean {
    for (let i = 0; i < 12; i++) if (this.seats && this.seats[i]) return true;
    return false;
  }

  getNumber(): number {
    return this.tid + 1;
  }

  getGameState() {
    if (!this.gameState) throw new Error('Error: no game state on this table.');
    return this.gameState;
  }

  setGameState(gameState: BaseGameState) {
    this.gameState = gameState;
  }

  getCost() {
    return this.cost;
  }

  getTimePerMoveString(time: number, postfix: string) {
    let message = '';

    if (!time || time <= 0) time = this.timePerMove;

    switch (time) {
      case 20:
        message += '1/3';
        break;
      case 30:
        message += '1/2';
        break;
      case 60:
        message += '1';
        break;
      case 2 * 60:
        message += '2';
        break;
      case 4 * 60:
        message += '4';
        break;
      case 5 * 60:
        message += '5';
        break;
      case 7 * 60:
        message += '7';
        break;
      case 10 * 60:
        message += '10';
        break;
      default:
        message += 'unknown';
    }
    return message + ' min' + (postfix ? postfix : '');
  }

  abstract getMatchStringLong(): string;

  getMatchMessage() {
    let message = '';
    message += t('table') + ' ' + this.getNumber();
    message += '\n' + this.getMatchStringLong();
    message += '\n' + this.getTimePerMoveString(0, '/move');
    return message;
  }

  resetGameState() {
    if (this.gameState) {
      this.gameState.onShutdown();
      this.gameState = undefined;
    }
    this.clearChatMessages();
  }

  convertInt(val: number, base: number, isDec: boolean): string {
    return val < base ? (isDec ? '0.5' : '1/2') : Math.floor(val / base).toString();
  }

  getTicketString() {
    if (this.cost === 0) return '';
    return 'T$: ' + this.convertInt(this.cost, 2, false);
  }

  showProjectedRating(me: IPlayer, opponent: IPlayer | undefined): void {
    if (this.ratingGame && opponent) {
      const opponentRating = opponent.getRating();
      const message =
        t('your_projected_rating') + ': ' + 
        t(
          'you_win_arg_you_lose_arg',
          me.projectRating('win', opponentRating).toString(),
          me.projectRating('loose', opponentRating).toString()
        );
      var newMsg = new Message({
        type: Codes.MessageTypes.GOD,
        text: message,
      });
      this.addChatMessage(newMsg);
    }
  }

  abstract getMatchString(): string;

  isPhantomTable() {
    return false;
  }
}
